/**
 * WebP Utility Functions
 * 
 * Utility functions for handling WebP image conversion and detection.
 * These functions can be used throughout the application for consistent WebP handling.
 */

/**
 * Converts an image URL to WebP format
 * @param {string} imageUrl - The original image URL
 * @returns {string} - The WebP version of the image URL
 */
export const convertImageToWebP = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return imageUrl;
  }
  
  // Check if it's already a WebP image
  if (imageUrl.toLowerCase().includes('.webp')) {
    return imageUrl;
  }
  
  // Convert common image extensions to WebP
  // Handles URLs with query parameters
  const webpUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|bmp|tiff)(\?.*)?$/i, '.webp$2');
  
  return webpUrl;
};

/**
 * Checks if the browser supports WebP format
 * @returns {Promise<boolean>} - Promise that resolves to true if WebP is supported
 */
export const checkWebPSupport = () => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    
    try {
      const dataURL = canvas.toDataURL('image/webp');
      resolve(dataURL.indexOf('data:image/webp') === 0);
    } catch (error) {
      resolve(false);
    }
  });
};

/**
 * Synchronously checks if the browser supports WebP format
 * @returns {boolean} - True if WebP is supported
 */
export const checkWebPSupportSync = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  try {
    const dataURL = canvas.toDataURL('image/webp');
    return dataURL.indexOf('data:image/webp') === 0;
  } catch (error) {
    return false;
  }
};

/**
 * Loads an image and returns a promise
 * @param {string} imageUrl - The image URL to load
 * @returns {Promise<string>} - Promise that resolves to the image URL when loaded
 */
export const loadImage = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve(imageUrl);
    };
    
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Attempts to load WebP version of an image with fallback to original
 * @param {string} originalUrl - The original image URL
 * @param {boolean} webpSupported - Whether WebP is supported (optional, will check if not provided)
 * @returns {Promise<string>} - Promise that resolves to the working image URL
 */
export const loadImageWithWebPFallback = async (originalUrl, webpSupported = null) => {
  if (!originalUrl) {
    throw new Error('Image URL is required');
  }

  // Check WebP support if not provided
  if (webpSupported === null) {
    webpSupported = checkWebPSupportSync();
  }

  try {
    // Try WebP first if supported
    if (webpSupported) {
      const webpUrl = convertImageToWebP(originalUrl);
      if (webpUrl !== originalUrl) {
        try {
          await loadImage(webpUrl);
          return webpUrl;
        } catch (webpError) {
          console.warn('WebP image failed to load, falling back to original:', webpError.message);
        }
      }
    }

    // Fallback to original image
    await loadImage(originalUrl);
    return originalUrl;
  } catch (error) {
    throw new Error(`All image formats failed to load: ${error.message}`);
  }
};

/**
 * Creates a picture element with WebP source and fallback
 * @param {string} originalUrl - The original image URL
 * @param {string} alt - Alt text for the image
 * @param {string} className - CSS classes
 * @param {object} props - Additional props for the img element
 * @returns {object} - React element for picture with WebP support
 */
export const createWebPPictureElement = (originalUrl, alt = '', className = '', props = {}) => {
  const webpUrl = convertImageToWebP(originalUrl);
  
  return {
    type: 'picture',
    props: {
      children: [
        {
          type: 'source',
          props: {
            srcSet: webpUrl,
            type: 'image/webp'
          }
        },
        {
          type: 'img',
          props: {
            src: originalUrl,
            alt,
            className,
            ...props
          }
        }
      ]
    }
  };
};

/**
 * Batch converts multiple image URLs to WebP format
 * @param {string[]} imageUrls - Array of image URLs to convert
 * @returns {string[]} - Array of WebP image URLs
 */
export const batchConvertToWebP = (imageUrls) => {
  if (!Array.isArray(imageUrls)) {
    return [];
  }
  
  return imageUrls.map(url => convertImageToWebP(url));
};

/**
 * Preloads WebP images for better performance
 * @param {string[]} imageUrls - Array of image URLs to preload
 * @param {boolean} webpSupported - Whether WebP is supported
 * @returns {Promise<void>} - Promise that resolves when all images are preloaded
 */
export const preloadWebPImages = async (imageUrls, webpSupported = null) => {
  if (!Array.isArray(imageUrls) || imageUrls.length === 0) {
    return;
  }

  // Check WebP support if not provided
  if (webpSupported === null) {
    webpSupported = checkWebPSupportSync();
  }

  const preloadPromises = imageUrls.map(async (url) => {
    try {
      const finalUrl = webpSupported ? convertImageToWebP(url) : url;
      await loadImage(finalUrl);
    } catch (error) {
      console.warn(`Failed to preload image: ${url}`, error);
    }
  });

  await Promise.allSettled(preloadPromises);
};
